import { useEffect, useRef } from 'react'
import { clearAuthRedirectUrl, getLoginAuthorizationParams, useAuth } from 'react-auth0'
import { useRouteMatch } from 'react-router-dom'
import { storeBranchReturnUrl } from 'shared/util/routing'

// the email arrives percent-encoded (admin builds the link with encodeURIComponent) and
// react-router v5 doesn't decode params, so Auth0 would get `joe%40x.com` as the hint
function decodeSlug(slug: string) {
  try {
    return decodeURIComponent(slug)
  } catch (err) {
    // a malformed escape (e.g. `%GG`) — pass it through so Auth0 can still show its own
    // error, but say why the prefill will look wrong
    console.warn(`[Login] could not decode login hint "${slug}", using it as-is`, err)
    return slug
  }
}

/**
 * Logs a user straight in — the target of the admin app's "Open PaintScout" button.
 */
export default function Login() {
  const { auth0Client } = useAuth()
  const match = useRouteMatch<{ slug?: string }>()
  const redirecting = useRef(false)

  useEffect(() => {
    if (redirecting.current) return
    redirecting.current = true

    // Auth0 can only return to the canonical origin (its allowed callback URLs have no
    // `/_branch/` entries), so stash the branch prefix for the callback to return to.
    // Deliberately the branch *root*, not this URL: redirecting back to /login/:slug
    // would remount this component and fire another loginWithRedirect — and now that
    // Auth0 has a live SSO session, that loops forever. No-op on canonical deploys.
    //
    // Clearing authRedirectUrl matters because it survives logout and takes precedence
    // over the stash (see consumeBranchRedirectUrl), so a leftover path from another
    // branch would otherwise win over this fresh login.
    clearAuthRedirectUrl()
    storeBranchReturnUrl()

    auth0Client.loginWithRedirect({
      authorizationParams: getLoginAuthorizationParams(
        match?.params?.slug
          ? {
              login_hint: decodeSlug(match.params.slug)
            }
          : {}
      )
    })
  }, [auth0Client, match?.params?.slug])

  return null
}
